/**
 * Datadog RUM (Real User Monitoring) integration
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/
 */

import { datadogRum } from "@datadog/browser-rum";

/**
 * Initialize Datadog RUM tracking
 * @param applicationId - Your Datadog Application ID
 * @param clientToken - Your Datadog Client Token
 * @param site - Datadog site (e.g., 'datadoghq.com', 'datadoghq.eu')
 * @param service - Service name for this application
 * @param env - Environment (e.g., 'production', 'staging', 'development')
 * @param version - Application version
 */
export function initializeDatadog(config: {
  applicationId: string;
  clientToken: string;
  site?: string;
  service: string;
  env: string;
  version?: string;
}): void {
  if (typeof window === "undefined") {
    console.warn("Datadog: window is undefined, skipping initialization");
    return;
  }

  if (datadogRum.getInternalContext()) {
    console.warn("Datadog: already initialized");
    return;
  }

  datadogRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: config.site || "datadoghq.com",
    service: config.service,
    env: config.env,
    version: config.version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20, // 20% of sessions will include replay
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
  });

  datadogRum.startSessionReplayRecording();
}

/**
 * Identify a user in Datadog with custom attributes
 * @param userId - Unique user identifier
 * @param attributes - Additional user attributes (should include email, name, etc.)
 */
export function identifyDatadogUser(
  userId: string,
  attributes?: Record<string, string | number | boolean>,
): void {
  if (typeof window === "undefined") {
    console.warn("Datadog: window is undefined, skipping user identification");
    return;
  }

  // Set user in Datadog RUM
  datadogRum.setUser({
    id: userId,
    ...attributes,
  });

  // Add email domain context for filtering
  const email = attributes?.email as string | undefined;
  if (email) {
    datadogRum.setGlobalContextProperty(
      "email_domain",
      email.split("@")[1] || "unknown",
    );

    // Add user type context (internal vs external)
    datadogRum.setGlobalContextProperty(
      "user_type",
      email.endsWith("@monthly.la") ? "internal" : "external",
    );
  }
}

/**
 * Track a custom action in Datadog
 * @param actionName - Name of the action to track
 * @param context - Additional context for the action
 */
export function trackDatadogAction(
  actionName: string,
  context?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") {
    console.warn("Datadog: window is undefined, skipping action tracking");
    return;
  }

  datadogRum.addAction(actionName, context);
}

/**
 * Add custom context to Datadog RUM session
 * @param key - Context key
 * @param value - Context value
 */
export function addDatadogContext(
  key: string,
  value: string | number | boolean | object,
): void {
  if (typeof window === "undefined") {
    console.warn("Datadog: window is undefined, skipping context addition");
    return;
  }

  datadogRum.setGlobalContextProperty(key, value);
}

/**
 * Remove custom context from Datadog RUM session
 * @param key - Context key to remove
 */
export function removeDatadogContext(key: string): void {
  if (typeof window === "undefined") {
    console.warn("Datadog: window is undefined, skipping context removal");
    return;
  }

  datadogRum.removeGlobalContextProperty(key);
}

/**
 * Track an error in Datadog
 * @param error - Error object or message
 * @param context - Additional context for the error
 */
export function trackDatadogError(
  error: Error | string,
  context?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") {
    console.warn("Datadog: window is undefined, skipping error tracking");
    return;
  }

  datadogRum.addError(error, context);
}
